import React, { useRef } from "react";

export const KanbanBoard = ({ 
  stages = [], 
  items = [], 
  renderCard, 
  onItemMove, 
  onDragStart: externalOnDragStart,
  onDragEnd: externalOnDragEnd,
  statusKey = "status",
  className = "" 
}) => {
  const containerRef = useRef(null);
  const scrollInterval = useRef(null);

  const startScrolling = (speed) => {
    if (scrollInterval.current) return;
    scrollInterval.current = setInterval(() => {
      if (containerRef.current) {
        containerRef.current.scrollLeft += speed;
      }
    }, 20);
  };

  const stopScrolling = () => {
    if (scrollInterval.current) {
      clearInterval(scrollInterval.current);
      scrollInterval.current = null;
    }
  };

  const handleAutoScroll = (clientX) => {
    if (!containerRef.current) return;
    const { left, right } = containerRef.current.getBoundingClientRect();
    const scrollSpeed = 15;
    const threshold = 100;

    if (clientX > right - threshold) {
      startScrolling(scrollSpeed);
    } else if (clientX < left + threshold) {
      startScrolling(-scrollSpeed);
    } else {
      stopScrolling();
    }
  };

  const onDragStart = (e, item) => {
    e.dataTransfer.setData("itemId", item._id || item.id);
    e.dataTransfer.effectAllowed = "move";
    const target = e.currentTarget;
    setTimeout(() => {
      target.classList.add("opacity-20", "scale-95");
    }, 0);
    
    if (externalOnDragStart) externalOnDragStart(e, item);
  };

  const onDragEnd = (e, item) => {
    e.currentTarget.classList.remove("opacity-20", "scale-95");
    stopScrolling();
    
    if (externalOnDragEnd) externalOnDragEnd(e, item);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    handleAutoScroll(e.clientX);
  };

  const onDrop = (e, stageKey) => {
    e.preventDefault();
    stopScrolling();
    const itemId = e.dataTransfer.getData("itemId");
    if (itemId && onItemMove) {
      const item = items.find(i => (i._id || i.id) === itemId);
      if (item) {
        onItemMove(item, stageKey);
      }
    }
  };

  return (
    <div
      className={`overflow-x-auto pb-6 custom-scrollbar ${className}`}
      ref={containerRef}
      onDragOver={onDragOver}
    >
      <div className="flex gap-4 min-w-max h-full">
        {stages.map(stage => {
          const stageItems = items.filter(i => (i[statusKey] || "").toLowerCase() === stage.key.toLowerCase());
          
          return (
            <div 
              key={stage.key} 
              className="w-72 flex-shrink-0 flex flex-col h-full"
              onDragOver={onDragOver}
              onDrop={(e) => onDrop(e, stage.key)}
            >
              {/* Column Header */}
              <div className={`flex items-center justify-between p-1.5 px-4 rounded-lg border mb-3 shadow-sm transition-all hover:shadow-md ${stage.color || 'border-slate-200 bg-white text-slate-700'}`}>
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-semibold text-slate-700 tracking-tight">{stage.label}</span>
                </div>
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-white text-[11px] font-bold text-slate-500 shadow-sm border border-slate-100">
                  {stageItems.length}
                </div>
              </div>

              {/* Cards Area */}
              <div className="space-y-3 overflow-y-auto max-h-[70vh] pr-2 custom-scrollbar">
                {stageItems.map(item => (
                  <div 
                    key={item._id || item.id}
                    draggable={!!onItemMove && !item.isLocked && !item.isDisabled}
                    onDragStart={(e) => onDragStart(e, item)}
                    onDragEnd={(e) => onDragEnd(e, item)}
                  >
                    {renderCard(item)}
                  </div>
                ))}
                
                {stageItems.length === 0 && (
                  <div className="py-12 border-2 border-dashed border-slate-200 rounded-2xl flex flex-col items-center justify-center text-center px-4 bg-white/50">
                    <div className="w-8 h-8 bg-slate-100 rounded-full mb-2 flex items-center justify-center">
                      <span className="text-slate-300">∅</span>
                    </div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">No Items</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default KanbanBoard;
